import { KEYS } from './KeyboardKeys.js';

const NAVIGATION_KEYS = [
	KEYS.ESCAPE,
	KEYS.UP_ARROW,
	KEYS.DOWN_ARROW,
	KEYS.LEFT_ARROW,
	KEYS.RIGHT_ARROW,
	KEYS.PAGE_UP,
	KEYS.PAGE_DOWN,
	KEYS.HOME,
	KEYS.END,
];
const ACCEPT_KEYS = [
	KEYS.ENTER,
	KEYS.SPACE,
	KEYS.SPACEBAR,
];

type TSetFocus = (index: number) => void;
type TGetFocus = () => number;
type TGetLength = () => number;
type TSetState<TValue> = (value: TValue, reason: TStateSetReason, e?: React.KeyboardEvent<HTMLElement>) => void;
type TStateSetReason = 'acceptKey' | 'navigationKey' | 'escapeKey';
type TAxis = 'vertical' | 'horizontal';

interface IKeyboardDirection {
	setFocus: TSetFocus,
	getFocus: TGetFocus,
	getLength: TGetLength;
	pageSize?: number;
}

interface IKeyboardAxis {
	vertical?: IKeyboardDirection;
	horizontal?: IKeyboardDirection;
	page?: TAxis;
	homeEnd?: TAxis;
}

interface IKeyboardSelection {
	onSelect: () => void;
	toggle: TSetState<boolean>;
}

interface IMenuKeyboardHandlerProps {
	axis: IKeyboardAxis;
	selection: IKeyboardSelection;
}

export class KeyboardListHandler {
	
	private axis: IKeyboardAxis;

	private get pageAxis(): TAxis {
		return this.axis.page || 'vertical';
	}

	private get homeEndAxis(): TAxis {
		return this.axis.homeEnd || 'vertical'
	}

	private selection: IKeyboardSelection;

	constructor(props: IMenuKeyboardHandlerProps) {
		const { axis, selection } = props;
		this.selection = selection;
		this.axis = axis;
	}

	public onKeyDown = (e: React.KeyboardEvent<HTMLElement>, isOpen: boolean) => {
		const { key } = e;
		if (NAVIGATION_KEYS.indexOf(key as KEYS) !== -1) {
			e.preventDefault();
			e.stopPropagation();
			this.onNavigation(e, key, isOpen);
		} else if (ACCEPT_KEYS.indexOf(key as KEYS) !== -1 && !isOpen) {
			e.preventDefault();
			this.selection.toggle(true, 'acceptKey', e);
		} else if (key === KEYS.TAB) {
			this.selection.toggle(false, 'navigationKey');
		} else if (ACCEPT_KEYS.indexOf(key as KEYS) !== -1 && isOpen) {
			let valid = true;
			if (this.axis.vertical && (this.axis.vertical.getLength() <= 0 || this.axis.vertical.getFocus() < 0)) {
				valid = false;
			}
			if (this.axis.horizontal && (this.axis.horizontal.getLength() <= 0 || this.axis.horizontal.getFocus() < 0)) {
				valid = false;
			}
			if (valid) {
				e.stopPropagation();
				e.preventDefault();
				if (this.selection.onSelect) {
					this.selection.onSelect();
				}
			}
		}
	}

	private onNavigation(e: React.KeyboardEvent<HTMLElement>, key: string, isOpen: boolean) {
		switch (key) {
			case KEYS.ESCAPE:
				this.selection.toggle(false, 'escapeKey');
				break;
			case KEYS.UP_ARROW:
			case KEYS.DOWN_ARROW:
				this.onVerticalNavigation(e, key, isOpen);
				break;
			case KEYS.LEFT_ARROW:
			case KEYS.RIGHT_ARROW:
				this.onHorizontalNavigation(e, key, isOpen);
				break;
			case KEYS.PAGE_UP:
			case KEYS.PAGE_DOWN:
				if (this.pageAxis === 'horizontal') {
					this.onHorizontalNavigation(e, key, isOpen);
				}
				else {
					this.onVerticalNavigation(e, key, isOpen);
				}
				break;
			case KEYS.HOME:
			case KEYS.END:
				if (this.homeEndAxis === 'horizontal') {
					this.onHorizontalNavigation(e, key, isOpen);
				}
				else {
					this.onVerticalNavigation(e, key, isOpen);
				}
				break;
			default:
				break;
		}
	}

	private onVerticalNavigation(e: React.KeyboardEvent<HTMLElement>, key: string, isOpen: boolean) {
		if (!this.axis.vertical) return;
		const index = this.axis.vertical.getFocus();
		const length = this.axis.vertical.getLength();
		const pageSize = this.axis.vertical.pageSize || 5;
		this.onAxisNavigation(e, key, isOpen, index, length, pageSize, KEYS.UP_ARROW, KEYS.DOWN_ARROW, this.axis.vertical.setFocus);
	}

	private onHorizontalNavigation(e: React.KeyboardEvent<HTMLElement>, key: string, isOpen: boolean) {
		if (!this.axis.horizontal) return;
		const index = this.axis.horizontal.getFocus();
		const length = this.axis.horizontal.getLength();
		const pageSize = this.axis.horizontal.pageSize || 5;
		this.onAxisNavigation(e, key, isOpen, index, length, pageSize, KEYS.LEFT_ARROW, KEYS.RIGHT_ARROW, this.axis.horizontal.setFocus);
	}

	private onAxisNavigation(e: React.KeyboardEvent<HTMLElement>, key: string, isOpen: boolean, index: number, length: number, pageSize: number, arrowMinus: string, arrowPositive: string, setFocus: TSetFocus) {
		switch (key) {
			case arrowMinus:
				if (isOpen) {
					if (index === 0) {
						setFocus(length - 1);
					} else {
						setFocus(index - 1);
					}
				} else {
					this.selection.toggle(true, 'navigationKey', e);
				}
				break;
			case arrowPositive:
				if (isOpen) {
					setFocus((index + 1) % length);
				} else {
					this.selection.toggle(true, 'navigationKey', e);
				}
				break;
			case KEYS.PAGE_UP:
				if (isOpen) {
					if (index - pageSize < 0 && index !== 0) {
						setFocus(0);
					} else if (index - pageSize < 0) {
						setFocus(length - 1);
					} else {
						setFocus(index - pageSize);
					}
				}
				break;
			case KEYS.PAGE_DOWN:
				if (isOpen) {
					if (index === length - 1) {
						setFocus(0);
					} else if (index + pageSize > length - 1) {
						setFocus(length - 1);
					} else {
						setFocus((index + pageSize) % length);
					}
				}
				break;
			case KEYS.HOME:
				if (isOpen) {
					setFocus(0);
				}
				break;
			case KEYS.END:
				if (isOpen) {
					setFocus(length - 1);
				}
				break;
			default:
				break;
		}
	}
}